
import React, { useState, useEffect, useCallback } from "react";
import { User } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save, Shield, Users, Briefcase, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const positions = [
  "Sales Consultant",
  "Sales Development",
  "Customer Service",
  "Customer Experience",
  "Case Analyst",
  "Case Manager",
  "Case Assistant",
  "Finance Coordinator",
  "Finance Analyst",
  "Head of Operations",
  "CEO",
  "Operations Coordinator"
];

const departments = [
  { id: "sales", name: "Sales", positions: ["Sales Consultant", "Sales Development"] },
  { id: "customer_service", name: "Customer Service", positions: ["Customer Service", "Customer Experience"] },
  { id: "operations", name: "Operations", positions: ["Case Analyst", "Case Manager", "Case Assistant"] },
  { id: "finance", name: "Finance", positions: ["Finance Coordinator", "Finance Analyst"] },
  { id: "management", name: "Management", positions: ["Head of Operations", "CEO", "Operations Coordinator"] }
];

export default function SecuritySettings() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [securitySettings, setSecuritySettings] = useState({
    // Position-based permissions
    position_permissions: {
      "Sales Consultant": {
        clients: { create: true, view_all: false, edit_all: false },
        cases: { view_assigned: true, edit_assigned: true, view_all: false, edit_all: false }, // Added view_all, edit_all for consistency
        leads: { create: true, view_all: true, edit_all: true }
      },
      "Sales Development": {
        clients: { create: false, view_all: false, edit_all: false },
        cases: { view_assigned: true, edit_assigned: false, view_all: false, edit_all: false },
        leads: { create: true, view_all: true, edit_all: true }
      },
      "Customer Service": {
        clients: { create: false, view_all: false, edit_all: false },
        cases: { view_assigned: true, edit_assigned: true, view_all: false, edit_all: false },
        leads: { create: false, view_all: false, edit_all: false }
      },
      "Customer Experience": {
        clients: { create: false, view_all: false, edit_all: false },
        cases: { view_assigned: true, edit_assigned: true, view_all: false, edit_all: false },
        leads: { create: false, view_all: false, edit_all: false }
      },
      "Case Analyst": {
        clients: { create: false, view_all: false, edit_all: false },
        cases: { view_assigned: true, edit_assigned: true, view_all: false, edit_all: false },
        leads: { create: false, view_all: false, edit_all: false }
      },
      "Case Manager": {
        clients: { create: false, view_all: true, edit_all: false },
        cases: { view_assigned: false, edit_assigned: false, view_all: true, edit_all: true },
        leads: { create: false, view_all: false, edit_all: false }
      },
      "Case Assistant": {
        clients: { create: false, view_all: false, edit_all: false },
        cases: { view_assigned: true, edit_assigned: false, view_all: true, edit_all: false },
        leads: { create: false, view_all: false, edit_all: false }
      },
      "Finance Coordinator": {
        clients: { create: false, view_all: true, edit_all: false },
        cases: { view_assigned: false, edit_assigned: false, view_all: true, edit_all: false },
        leads: { create: false, view_all: false, edit_all: false }
      },
      "Finance Analyst": {
        clients: { create: false, view_all: true, edit_all: false },
        cases: { view_assigned: false, edit_assigned: false, view_all: true, edit_all: false },
        leads: { create: false, view_all: false, edit_all: false }
      },
      "Head of Operations": {
        clients: { create: true, view_all: true, edit_all: true },
        cases: { view_assigned: false, edit_assigned: false, view_all: true, edit_all: true },
        leads: { create: true, view_all: true, edit_all: true }
      },
      "CEO": {
        clients: { create: true, view_all: true, edit_all: true },
        cases: { view_assigned: false, edit_assigned: false, view_all: true, edit_all: true },
        leads: { create: true, view_all: true, edit_all: true }
      },
      "Operations Coordinator": {
        clients: { create: true, view_all: true, edit_all: true },
        cases: { view_assigned: false, edit_assigned: false, view_all: true, edit_all: true },
        leads: { create: true, view_all: true, edit_all: true }
      }
    }
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      setCurrentUser(userData);

      if (userData.role !== 'admin') {
        navigate(createPageUrl("Settings"));
        return;
      }

      // Load security settings from user entity
      if (userData.position_permissions) {
        setSecuritySettings(prev => ({
          ...prev, // Keep existing structure in case of partial updates
          position_permissions: {
            ...prev.position_permissions, // Merge with existing defaults
            ...userData.position_permissions // Override with loaded data
          }
        }));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Send the entire securitySettings object, which now contains position_permissions
      await User.updateMyUserData(securitySettings);
      await loadData();
    } catch (error) {
      console.error("Error saving security settings:", error);
    }
    setIsSaving(false);
  };

  const handlePermissionChange = (position, entity, permission, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      position_permissions: {
        ...prev.position_permissions,
        [position]: {
          ...prev.position_permissions[position],
          [entity]: {
            ...prev.position_permissions[position][entity],
            [permission]: value
          }
        }
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate(createPageUrl("Settings"))}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Security & Privacy</h1>
          <p className="text-slate-600">Configure position-based access and permissions</p>
        </div>

        <Card className="border-amber-200 bg-amber-50 mb-6">
          <CardContent className="p-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Changes here affect all users in your organization based on their position. Admin users always have full access regardless of these settings.
            </p>
          </CardContent>
        </Card>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Departments Overview - Kept as it provides context */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">Departments & Positions</CardTitle>
                  <CardDescription>Your organization structure</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {departments.map((dept) => (
                  <div key={dept.id} className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-slate-600" />
                      {dept.name}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {dept.positions.map((position) => (
                        <Badge key={position} variant="outline" className="text-xs">
                          {position}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Position-Based Permissions */}
          {positions.map((position) => (
            <Card key={position} className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{position}</CardTitle>
                    <CardDescription>Configure permissions for this position</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Client Permissions */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-4">Client Permissions</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can Create Clients</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.clients?.create || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'clients', 'create', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can View All Clients</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.clients?.view_all || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'clients', 'view_all', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can Edit All Clients</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.clients?.edit_all || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'clients', 'edit_all', checked)}
                      />
                    </div>
                  </div>
                </div>

                {/* Case Permissions */}
                <div className="border-t border-slate-200 pt-6">
                  <h4 className="font-semibold text-slate-700 mb-4">Case Permissions</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can View Assigned Cases</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.cases?.view_assigned || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'cases', 'view_assigned', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can Edit Assigned Cases</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.cases?.edit_assigned || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'cases', 'edit_assigned', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can View All Cases</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.cases?.view_all || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'cases', 'view_all', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can Edit All Cases</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.cases?.edit_all || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'cases', 'edit_all', checked)}
                      />
                    </div>
                  </div>
                </div>

                {/* Lead Permissions */}
                <div className="border-t border-slate-200 pt-6">
                  <h4 className="font-semibold text-slate-700 mb-4">Lead Permissions</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can Create Leads</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.leads?.create || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'leads', 'create', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can View All Leads</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.leads?.view_all || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'leads', 'view_all', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <Label>Can Edit All Leads</Label>
                      <Switch
                        checked={securitySettings.position_permissions[position]?.leads?.edit_all || false}
                        onCheckedChange={(checked) => handlePermissionChange(position, 'leads', 'edit_all', checked)}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={loadData} // Cancel reverts to last saved state
              disabled={isSaving}
              className="border-slate-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Security Settings'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
